import { useEffect, useState, useRef, useCallback, lazy, Suspense } from 'react';
import { useAtlasStore } from '@/stores/atlasStore';
import { fetchOrder, fetchCadastroBounds, fetchCogStatistics, fetchCogStatisticsClipped, fetchTileJSON, fetchUserProjectName, buildMdtTileUrl, buildMdtDemTileUrl, buildHillshadeTileUrl, buildHillshadeHypsoTileUrl, buildSlopeTileUrl, buildAspectTileUrl, buildGeomorphonsTileUrl, buildErosionRiskTileUrl, buildWindExpositionTileUrl, buildWindReliefTileUrl, buildFlowAccTileUrl } from '@/services/api';
import { MapView2D } from '@/components/MapView2D';
// 3D viewer (CesiumJS, ~3 MB) is code-split: its chunk only downloads when the
// user first clicks 3D. See the lazy-mount gate in the render block below.
const MapView3D = lazy(() =>
  import('@/components/MapView3D').then((m) => ({ default: m.MapView3D }))
);
import { ViewToggle } from '@/components/ViewToggle';
import { Toolbar } from '@/components/Toolbar';
import { LegendStrip } from '@/components/LegendStrip';
import { TopBar } from '@/components/TopBar';
import { OrderPanel } from '@/components/OrderPanel';
import { PaymentBanner } from '@/components/PaymentBanner';
import { Layers } from 'lucide-react';

type OrderMeta = {
  orderId: string;
  projectId: string | null;
  productsCount: number;
  areaHa: number | null;
};

function App() {
  const viewMode = useAtlasStore((s) => s.viewMode);
  // Mount the 3D viewer only once the user has entered 3D at least once, so
  // Cesium + the terrain tiles never download for a 2D-only session. Once
  // mounted it stays mounted (CSS-hidden) to avoid re-fetching terrain on every
  // 2D/3D toggle.
  const [threeDMounted, setThreeDMounted] = useState(false);
  useEffect(() => {
    if (viewMode === '3d') setThreeDMounted(true);
  }, [viewMode]);
  const loading = useAtlasStore((s) => s.loading);
  const error = useAtlasStore((s) => s.error);
  const setOrderPolygon = useAtlasStore((s) => s.setOrderPolygon);
  const setOrderBounds = useAtlasStore((s) => s.setOrderBounds);
  const setLoading = useAtlasStore((s) => s.setLoading);
  const setError = useAtlasStore((s) => s.setError);
  const setRasterAvailable = useAtlasStore((s) => s.setRasterAvailable);
  const setLayerLoading = useAtlasStore((s) => s.setLayerLoading);
  const setLoadLayer = useAtlasStore((s) => s.setLoadLayer);
  const setMdtTileUrl = useAtlasStore((s) => s.setMdtTileUrl);
  const setMdtDemTileUrl = useAtlasStore((s) => s.setMdtDemTileUrl);
  const setMdtRescale = useAtlasStore((s) => s.setMdtRescale);
  const setMdtZoomRange = useAtlasStore((s) => s.setMdtZoomRange);
  const setHillshadeTileUrl = useAtlasStore((s) => s.setHillshadeTileUrl);
  const setHillshadeZoomRange = useAtlasStore((s) => s.setHillshadeZoomRange);
  const setHillshadeHypsoTileUrl = useAtlasStore((s) => s.setHillshadeHypsoTileUrl);
  const setHillshadeHypsoZoomRange = useAtlasStore((s) => s.setHillshadeHypsoZoomRange);
  const setContoursTileUrl = useAtlasStore((s) => s.setContoursTileUrl);
  const setContoursSourceLayer = useAtlasStore((s) => s.setContoursSourceLayer);
  const setContoursItemId = useAtlasStore((s) => s.setContoursItemId);
  const setContoursDgtTileUrl = useAtlasStore((s) => s.setContoursDgtTileUrl);
  const setContoursDgtSourceLayer = useAtlasStore((s) => s.setContoursDgtSourceLayer);
  const setContoursDgtItemId = useAtlasStore((s) => s.setContoursDgtItemId);
  const setSlopeTileUrl = useAtlasStore((s) => s.setSlopeTileUrl);
  const setSlopeZoomRange = useAtlasStore((s) => s.setSlopeZoomRange);
  const setAspectTileUrl = useAtlasStore((s) => s.setAspectTileUrl);
  const setAspectZoomRange = useAtlasStore((s) => s.setAspectZoomRange);
  const setGeomorphonsTileUrl = useAtlasStore((s) => s.setGeomorphonsTileUrl);
  const setErosionRiskTileUrl = useAtlasStore((s) => s.setErosionRiskTileUrl);
  const setErosionRiskZoomRange = useAtlasStore((s) => s.setErosionRiskZoomRange);
  const setGeomorphonsZoomRange = useAtlasStore((s) => s.setGeomorphonsZoomRange);
  const setWindExpositionTileUrl = useAtlasStore((s) => s.setWindExpositionTileUrl);
  const setWindExpositionZoomRange = useAtlasStore((s) => s.setWindExpositionZoomRange);
  const setWindHillshadeTileUrl = useAtlasStore((s) => s.setWindHillshadeTileUrl);
  const setWindReliefTileUrl = useAtlasStore((s) => s.setWindReliefTileUrl);
  const setWindReliefZoomRange = useAtlasStore((s) => s.setWindReliefZoomRange);
  const setWindHillshadeZoomRange = useAtlasStore((s) => s.setWindHillshadeZoomRange);
  const setStreamsTileUrl = useAtlasStore((s) => s.setStreamsTileUrl);
  const setStreamsSourceLayer = useAtlasStore((s) => s.setStreamsSourceLayer);
  const setStreamsItemId = useAtlasStore((s) => s.setStreamsItemId);
  const setRidgesTileUrl = useAtlasStore((s) => s.setRidgesTileUrl);
  const setRidgesSourceLayer = useAtlasStore((s) => s.setRidgesSourceLayer);
  const setRidgesItemId = useAtlasStore((s) => s.setRidgesItemId);
  const setBasinsTileUrl = useAtlasStore((s) => s.setBasinsTileUrl);
  const setBasinsSourceLayer = useAtlasStore((s) => s.setBasinsSourceLayer);
  const setBasinsItemId = useAtlasStore((s) => s.setBasinsItemId);
  const setKeypointsTileUrl = useAtlasStore((s) => s.setKeypointsTileUrl);
  const setKeypointsSourceLayer = useAtlasStore((s) => s.setKeypointsSourceLayer);
  const setKeypointsItemId = useAtlasStore((s) => s.setKeypointsItemId);
  const setPrimaryValleysTileUrl = useAtlasStore((s) => s.setPrimaryValleysTileUrl);
  const setPrimaryValleysSourceLayer = useAtlasStore((s) => s.setPrimaryValleysSourceLayer);
  const setPrimaryValleysItemId = useAtlasStore((s) => s.setPrimaryValleysItemId);
  const setPrimaryRidgesTileUrl = useAtlasStore((s) => s.setPrimaryRidgesTileUrl);
  const setPrimaryRidgesSourceLayer = useAtlasStore((s) => s.setPrimaryRidgesSourceLayer);
  const setPrimaryRidgesItemId = useAtlasStore((s) => s.setPrimaryRidgesItemId);
  const setFloodZonesTileUrl = useAtlasStore((s) => s.setFloodZonesTileUrl);
  const setFloodZonesSourceLayer = useAtlasStore((s) => s.setFloodZonesSourceLayer);
  const setFloodZonesItemId = useAtlasStore((s) => s.setFloodZonesItemId);
  const setFlowAccTileUrl = useAtlasStore((s) => s.setFlowAccTileUrl);
  const setFlowAccZoomRange = useAtlasStore((s) => s.setFlowAccZoomRange);
  // Institutional setters (PR5a). Globals always populated; restricoes
  // setters swap to order-clipped source when an order has the item.
  const setCadastroDgtTileUrl = useAtlasStore((s) => s.setCadastroDgtTileUrl);
  const setCadastroBounds = useAtlasStore((s) => s.setCadastroBounds);
  const setBupiRggTileUrl = useAtlasStore((s) => s.setBupiRggTileUrl);
  const setFreguesiasTileUrl = useAtlasStore((s) => s.setFreguesiasTileUrl);
  const setConcelhosTileUrl = useAtlasStore((s) => s.setConcelhosTileUrl);
  const setRenTileUrl = useAtlasStore((s) => s.setRenTileUrl);
  const setRenSourceLayer = useAtlasStore((s) => s.setRenSourceLayer);
  const setRenItemId = useAtlasStore((s) => s.setRenItemId);
  const setRenExclusoesTileUrl = useAtlasStore((s) => s.setRenExclusoesTileUrl);
  const setRenExclusoesSourceLayer = useAtlasStore((s) => s.setRenExclusoesSourceLayer);
  const setRenExclusoesItemId = useAtlasStore((s) => s.setRenExclusoesItemId);
  const setRanTileUrl = useAtlasStore((s) => s.setRanTileUrl);
  const setRanSourceLayer = useAtlasStore((s) => s.setRanSourceLayer);
  const setRanItemId = useAtlasStore((s) => s.setRanItemId);

  // Parse order ID from URL
  const orderId = new URLSearchParams(window.location.search).get('order');

  // Raw order result, kept so raster layers can be fetched lazily on first toggle.
  const orderResultRef = useRef<any>(null);

  // TopBar metadata — populated from fetchOrder, then enriched with project
  // name if order.project_id is set. Independent of the layer-tile state in
  // the zustand store; lives here so it stays scoped to the order load.
  const [orderMeta, setOrderMeta] = useState<OrderMeta | null>(null);
  const [projectName, setProjectName] = useState<string | null>(null);

  // Resolve project name when meta arrives + projectId is set.
  useEffect(() => {
    if (!orderMeta?.projectId) {
      setProjectName(null);
      return;
    }
    let cancelled = false;
    fetchUserProjectName(orderMeta.projectId).then((name) => {
      if (!cancelled) setProjectName(name);
    });
    return () => {
      cancelled = true;
    };
  }, [orderMeta?.projectId]);

  // Fetch order on mount
  useEffect(() => {
    if (!orderId) {
      // No order: empty atlas shell (base map + TopBar switcher). The
      // authenticated client picks a project/order from the TopBar instead
      // of seeing an error.
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    fetchOrder(orderId)
      .then((result) => {
        if (!result) {
          setError('Pedido nao encontrado');
          return;
        }
        setOrderMeta(result.meta);
        setOrderPolygon(result.polygon);
        setOrderBounds(result.bounds);

        // Keep the order result so raster layers can be fetched lazily on
        // first toggle (see loadLayer). EB-56: nothing raster is fetched on
        // load; we only record which products exist so the toolbar can show
        // their rows. Vector/institutional layers (Martin MVT, no cold COG
        // read) keep setting their URLs below and are added to the map only
        // when toggled on (gated in MapView2D.addAllLayers).
        orderResultRef.current = result;
        setRasterAvailable({
          mdt: !!result.mdtTileUrl,
          hillshade: !!result.hillshadeTileUrl,
          hypso: !!result.hillshadeHypsoTileUrl,
          slope: !!result.slopeCogPath,
          aspect: !!result.aspectCogPath,
          geomorphons: !!result.geomorphonsCogPath,
          wind: !!result.windExpositionCogPath,
          erosion: !!result.erosionRiskCogPath,
          flow: !!result.flowAccCogPath,
        });

        // Set contours AWS tile URL if available (vector tiles via Martin)
        if (result.contoursTileUrl) {
          setContoursTileUrl(result.contoursTileUrl);
          setContoursSourceLayer(result.contoursSourceLayer);
          setContoursItemId(result.contoursItemId);
          console.log(`Contours AWS layer ready: ${result.contoursSourceLayer}`);
        }

        // Set contours DGT tile URL if available (vector tiles via Martin — LOLA 50cm/2m)
        if (result.contoursDgtTileUrl) {
          setContoursDgtTileUrl(result.contoursDgtTileUrl);
          setContoursDgtSourceLayer(result.contoursDgtSourceLayer);
          setContoursDgtItemId(result.contoursDgtItemId);
          console.log(`Contours DGT layer ready: ${result.contoursDgtSourceLayer}`);
        }

        // Set streams tile URL if available (vector tiles via Martin)
        if (result.streamsTileUrl) {
          setStreamsTileUrl(result.streamsTileUrl);
          setStreamsSourceLayer(result.streamsSourceLayer);
          setStreamsItemId(result.streamsItemId);
          console.log(`Streams layer ready: ${result.streamsSourceLayer}`);
        }

        // Set ridges tile URL if available (vector tiles via Martin)
        if (result.ridgesTileUrl) {
          setRidgesTileUrl(result.ridgesTileUrl);
          setRidgesSourceLayer(result.ridgesSourceLayer);
          setRidgesItemId(result.ridgesItemId);
          console.log(`Ridges layer ready: ${result.ridgesSourceLayer}`);
        }

        // Set basins tile URL if available (vector tiles via Martin)
        if (result.basinsTileUrl) {
          setBasinsTileUrl(result.basinsTileUrl);
          setBasinsSourceLayer(result.basinsSourceLayer);
          setBasinsItemId(result.basinsItemId);
          console.log(`Basins layer ready: ${result.basinsSourceLayer}`);
        }

        // Set keypoints tile URL if available (vector tiles via Martin)
        if (result.keypointsTileUrl) {
          setKeypointsTileUrl(result.keypointsTileUrl);
          setKeypointsSourceLayer(result.keypointsSourceLayer);
          setKeypointsItemId(result.keypointsItemId);
          console.log(`Keypoints layer ready: ${result.keypointsSourceLayer}`);
        }

        // Set primary valleys tile URL if available (vector tiles via Martin)
        if (result.primaryValleysTileUrl) {
          setPrimaryValleysTileUrl(result.primaryValleysTileUrl);
          setPrimaryValleysSourceLayer(result.primaryValleysSourceLayer);
          setPrimaryValleysItemId(result.primaryValleysItemId);
          console.log(`Primary valleys layer ready: ${result.primaryValleysSourceLayer}`);
        }

        // Set primary ridges tile URL if available (vector tiles via Martin)
        if (result.primaryRidgesTileUrl) {
          setPrimaryRidgesTileUrl(result.primaryRidgesTileUrl);
          setPrimaryRidgesSourceLayer(result.primaryRidgesSourceLayer);
          setPrimaryRidgesItemId(result.primaryRidgesItemId);
          console.log(`Primary ridges layer ready: ${result.primaryRidgesSourceLayer}`);
        }

        // Set flood zones tile URL if available (vector tiles via Martin)
        if (result.floodZonesTileUrl) {
          setFloodZonesTileUrl(result.floodZonesTileUrl);
          setFloodZonesSourceLayer(result.floodZonesSourceLayer);
          setFloodZonesItemId(result.floodZonesItemId);
          console.log(`Flood zones layer ready: ${result.floodZonesSourceLayer}`);
        }

        // Institutional layers (PR5a). Globals always populated regardless of
        // order content. Restricoes-driven swap was decided by api.ts.
        setCadastroDgtTileUrl(result.cadastroDgtTileUrl);
        // Bound the Cadastro layer to the union extent of parcels intersecting
        // the order (polygon + 500m). The layer add waits for this; fall back
        // to the order bbox on null/error so it is never unbounded (full
        // country) again. result.bounds is [w,s,e,n] in 4326, same as the API.
        fetchCadastroBounds(result.polygon, 500).then((b) =>
          setCadastroBounds(b ?? (result.bounds as [number, number, number, number])),
        );
        setBupiRggTileUrl(result.bupiRggTileUrl);
        setFreguesiasTileUrl(result.caopAdminTileUrl);
        setConcelhosTileUrl(result.caopAdminTileUrl);
        setRenTileUrl(result.renTileUrl);
        setRenSourceLayer(result.renSourceLayer);
        setRenItemId(result.renItemId);
        setRenExclusoesTileUrl(result.renTileUrl);
        setRenExclusoesSourceLayer(result.renSourceLayer);
        setRenExclusoesItemId(result.renItemId);
        setRanTileUrl(result.ranTileUrl);
        setRanSourceLayer(result.ranSourceLayer);
        setRanItemId(result.ranItemId);
        console.log(
          result.renItemId
            ? `Restricoes Legais ready (clipped to order item ${result.renItemId})`
            : `Institutional layers ready (global context, no order clip)`,
        );
      })
      .catch((err) => {
        setError(`Erro ao carregar pedido: ${err.message}`);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy raster loader: fetch a raster product's metadata + build its tile URL
  // only when the user first toggles it on (EB-56). Clears its loading flag
  // when done (the cold COG statistics read is the real wait; tiles paint
  // within a few hundred ms after).
  const loadLayer = useCallback((key: string) => {
    const result = orderResultRef.current;
    if (!result) return;
    (async () => {
      try {
        switch (key) {
          case 'mdt': {
            if (!result.mdtTileUrl) break;
            // Clip statistics to the order polygon: the COG covers whole DGT
            // tiles well beyond the parcel, and a whole-raster min/max
            // compresses the parcel's own relief into a narrow slice of the
            // colormap. Fall back to whole-COG stats if the POST fails.
            const stats =
              (await fetchCogStatisticsClipped(result.mdtTileUrl, result.polygon)) ??
              (await fetchCogStatistics(result.mdtTileUrl));
            // Scale the ramp to the parcel's p2..p98 band, not raw min..max.
            // The COG min is dragged to ~0 by the transparent clip-buffer edge
            // (and the clipped-stats POST currently 503s, so we fall back to
            // whole-COG stats), which pushed the whole parcel into the warm top
            // of spectral_r ("all reddish"). p2/p98 drop those tails and restore
            // the full blue->red spread across the real terrain range.
            const lo = stats ? (stats.p2 ?? stats.min) : 0;
            const hi = stats ? (stats.p98 ?? stats.max) : 500;
            const min = Math.floor(lo);
            const max = Math.ceil(hi);
            const rescale = `${min},${max}`;
            setMdtTileUrl(buildMdtTileUrl(result.mdtTileUrl, rescale));
            // Relief overlay: same COG, Terrarium-encoded for the MapLibre
            // hillshade layer (see MapView2D mdt-hillshade-layer).
            setMdtDemTileUrl(buildMdtDemTileUrl(result.mdtTileUrl));
            setMdtRescale(rescale);
            const zr = await fetchTileJSON(result.mdtTileUrl, rescale);
            if (zr) setMdtZoomRange(zr);
            break;
          }
          case 'hillshade': {
            if (!result.hillshadeTileUrl) break;
            const rescale = '0,255';
            setHillshadeTileUrl(buildHillshadeTileUrl(result.hillshadeTileUrl, rescale));
            const zr = await fetchTileJSON(result.hillshadeTileUrl, rescale);
            if (zr) setHillshadeZoomRange(zr);
            break;
          }
          case 'hypso': {
            if (!result.hillshadeHypsoTileUrl) break;
            setHillshadeHypsoTileUrl(buildHillshadeHypsoTileUrl(result.hillshadeHypsoTileUrl));
            const zr = await fetchTileJSON(result.hillshadeHypsoTileUrl, '0,255');
            if (zr) setHillshadeHypsoZoomRange(zr);
            break;
          }
          case 'slope': {
            if (!result.slopeCogPath) break;
            setSlopeTileUrl(buildSlopeTileUrl(result.slopeCogPath));
            const zr = await fetchTileJSON(result.slopeCogPath, '0,45');
            if (zr) setSlopeZoomRange(zr);
            break;
          }
          case 'aspect': {
            if (!result.aspectCogPath) break;
            setAspectTileUrl(buildAspectTileUrl(result.aspectCogPath));
            const zr = await fetchTileJSON(result.aspectCogPath, '0,360');
            if (zr) setAspectZoomRange(zr);
            break;
          }
          case 'geomorphons': {
            if (!result.geomorphonsCogPath) break;
            setGeomorphonsTileUrl(buildGeomorphonsTileUrl(result.geomorphonsCogPath));
            const zr = await fetchTileJSON(result.geomorphonsCogPath, '1,10');
            if (zr) setGeomorphonsZoomRange(zr);
            break;
          }
          case 'wind': {
            if (!result.windExpositionCogPath) break;
            // Prefer the worker-baked relief composite (WEI colour x hillshade,
            // single RGBA passthrough). Orders processed before that shipped have
            // no relief file, so probe it and fall back to the legacy two-layer
            // render (60% discrete colour over a grey hillshade) for them.
            // fetchTileJSON returns null on a 404, which is the probe.
            const reliefZr = result.windReliefCogPath
              ? await fetchTileJSON(result.windReliefCogPath, '0,255')
              : null;
            if (result.windReliefCogPath && reliefZr) {
              setWindReliefTileUrl(buildWindReliefTileUrl(result.windReliefCogPath));
              setWindReliefZoomRange(reliefZr);
              break;
            }
            setWindExpositionTileUrl(buildWindExpositionTileUrl(result.windExpositionCogPath));
            fetchTileJSON(result.windExpositionCogPath, '0.7,1.3').then((zr) => { if (zr) setWindExpositionZoomRange(zr); });
            if (result.windHillshadeCogPath) {
              const rescale = '0,255';
              setWindHillshadeTileUrl(buildHillshadeTileUrl(result.windHillshadeCogPath, rescale));
              const zr = await fetchTileJSON(result.windHillshadeCogPath, rescale);
              if (zr) setWindHillshadeZoomRange(zr);
            }
            break;
          }
          case 'erosion': {
            if (!result.erosionRiskCogPath) break;
            // p98 rescale (right-skewed STI); see prior inline note.
            const eroStats = await fetchCogStatistics(result.erosionRiskCogPath);
            const eMax = eroStats ? Math.max(1, Math.ceil(eroStats.p98)) : 13;
            const eroRescale = `0,${eMax}`;
            setErosionRiskTileUrl(buildErosionRiskTileUrl(result.erosionRiskCogPath, eroRescale));
            const zr = await fetchTileJSON(result.erosionRiskCogPath, eroRescale);
            if (zr) setErosionRiskZoomRange(zr);
            break;
          }
          case 'flow': {
            if (!result.flowAccCogPath) break;
            // EB-81 v0.2: fixed log10 rescale 2..4 (raw ~100..10000, Clemente's
            // classification) so the transparent-low colormap cut sits at a
            // consistent ~raw 110 regardless of the parcel's max accumulation.
            const flowRescale = '2,4';
            setFlowAccTileUrl(buildFlowAccTileUrl(result.flowAccCogPath, flowRescale));
            const zr = await fetchTileJSON(result.flowAccCogPath, flowRescale);
            if (zr) setFlowAccZoomRange(zr);
            break;
          }
        }
      } catch (e) {
        console.warn('loadLayer error', key, e);
      } finally {
        setLayerLoading(key, false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Expose the lazy loader to the toolbar (which renders prop-less from store).
  useEffect(() => {
    setLoadLayer(loadLayer);
  }, [loadLayer, setLoadLayer]);

  // Camera sync on mode switch — deactivated (3D disabled)

  // Empty-state hint: an order is loaded but no layer is selected yet (EB-56).
  const anyLayerVisible = useAtlasStore((s) =>
    s.mdtVisible || s.hillshadeVisible || s.hillshadeHypsoVisible || s.contoursVisible ||
    s.slopeVisible || s.aspectVisible || s.geomorphonsVisible || s.windExpositionVisible ||
    s.erosionRiskVisible || s.flowAccVisible || s.streamsVisible || s.ridgesVisible ||
    s.basinsVisible || s.floodZonesVisible || s.keypointsVisible || s.primaryValleysVisible ||
    s.primaryRidgesVisible || s.cadastroDgtVisible || s.bupiRggVisible || s.freguesiasVisible ||
    s.concelhosVisible || s.renVisible || s.renExclusoesVisible || s.ranVisible ||
    s.roadsVisible || s.buildingsVisible || s.landuseVisible || s.waterwaysVisible || s.adminBoundsVisible,
  );
  const createMode = useAtlasStore((s) => s.createMode);

  return (
    <div className="relative w-full h-full">
      {/* 2D Map — always mounted, hidden via CSS when not active */}
      <div style={{ display: viewMode === '2d' ? 'block' : 'none', width: '100%', height: '100%' }}>
        <MapView2D />
      </div>

      {/* 3D Map — mounted lazily on first 3D activation (Cesium + terrain only
          download when 3D is clicked). Per-layer COG/vector data still loads on
          toggle via the shared store (loadLayer), same as 2D. */}
      {threeDMounted && (
        <div style={{ display: viewMode === '3d' ? 'block' : 'none', width: '100%', height: '100%' }}>
          <Suspense fallback={null}>
            <MapView3D />
          </Suspense>
        </div>
      )}

      {/* Brand chrome at the top — wordmark, project pill, action placeholders */}
      <TopBar
        orderId={orderMeta?.orderId ?? null}
        productsCount={orderMeta?.productsCount ?? null}
        areaHa={orderMeta?.areaHa ?? null}
        projectName={projectName}
      />

      {/* Controls overlay — sits below the TopBar (h-12 = 48px, top-14 = 56px) */}
      <div className="absolute top-14 left-4 z-10 flex flex-col gap-2">
        <ViewToggle />
        <Toolbar />
      </div>

      {/* Legend strip — bottom-right, auto-shows for active layers */}
      <LegendStrip />

      {/* Empty-state hint — order loaded but no layer selected yet (EB-56) */}
      {orderId && !loading && !error && !createMode && !anyLayerVisible && (
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 z-10 flex justify-center px-4 pointer-events-none">
          <div className="bg-schist/95 text-limestone rounded-lg shadow-lg shadow-black/20 px-4 py-3 flex items-center gap-3 max-w-xs">
            <Layers size={20} className="text-terracotta shrink-0" />
            <div>
              <div className="text-sm font-semibold">Selecione uma camada</div>
              <div className="text-[11px] text-clay leading-snug">Escolha uma camada na barra lateral para ver a analise do terreno.</div>
            </div>
          </div>
        </div>
      )}

      {/* In-map "novo pedido" panel — self-gates on createMode */}
      <OrderPanel />

      {/* Payment-return banner — self-gates on ?payment= (Stripe redirect) */}
      <PaymentBanner />

      {/* Loading spinner */}
      {loading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/50">
          <div className="bg-schist rounded-lg p-6 shadow-xl shadow-black/30 flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-clay/30 border-t-terracotta" />
            <p className="text-sm text-clay">A carregar pedido...</p>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && !loading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/50">
          <div className="bg-schist rounded-lg p-6 shadow-xl shadow-black/30 max-w-sm">
            <p className="text-terracotta font-medium mb-1">Erro</p>
            <p className="text-sm text-clay">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
